/**
 * Auth context — Firebase Auth gate for the SPA.
 * Sends Firebase ID tokens to `/api/**`; never stores API gate secrets.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  GithubAuthProvider,
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth';
import { getFirebaseAuth } from './firebase';

const EMAIL_FOR_SIGN_IN_KEY = 'zunelo_email_for_sign_in';

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  getIdToken: () => Promise<string | null>;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLinkIfPresent: () => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function mapAuthError(err: unknown): string {
  const code =
    err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return 'Sign-in cancelled.';
  }
  if (code === 'auth/operation-not-allowed') {
    return 'That sign-in method is not enabled for this Firebase project yet.';
  }
  if (code === 'auth/invalid-email') {
    return 'Invalid email address.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'This domain is not authorized for Firebase Auth.';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'Sign in with the method already associated with this email.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Authentication failed.';
}

export const DEV_MOCK_USER: User = {
  uid: 'dev-agent-user',
  email: 'agent@zunelo.local',
  displayName: 'Agent Dev',
  emailVerified: true,
  isAnonymous: false,
  metadata: {},
  providerData: [],
  refreshToken: '',
  tenantId: null,
  delete: async () => {},
  getIdToken: async () => 'dev-mock-id-token',
  getIdTokenResult: async () =>
    ({
      token: 'dev-mock-id-token',
      authTime: '0',
      issuedAtTime: '0',
      expirationTime: '0',
      signInProvider: 'mock',
      signInSecondFactor: null,
      claims: {},
    }) as never,
  reload: async () => {},
  toJSON: () => ({}),
  phoneNumber: null,
  photoURL: null,
  providerId: 'firebase',
} as unknown as User;

function isDevAuthBypassEnabled(): boolean {
  const isDevOrExplicitBypass =
    import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  if (!isDevOrExplicitBypass) return false;

  // Environment variable override (e.g. CI preview channels or dedicated dev scripts)
  if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'true') return true;
  if (import.meta.env.VITE_DEV_AUTH_BYPASS === 'false') return false;

  // Explicit URL query parameter requirement (?agent=true, ?mockAuth=true, ?bot=true)
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('mockAuth') === 'true' ||
      params.get('agent') === 'true' ||
      params.get('bot') === 'true'
    ) {
      return true;
    }
  }

  // Default behavior remains normal Firebase authentication
  return false;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isBypass = useMemo(() => isDevAuthBypassEnabled(), []);
  const [user, setUser] = useState<User | null>(() => (isBypass ? DEV_MOCK_USER : null));
  const [loading, setLoading] = useState(!isBypass);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isBypass) return;
    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (next) => {
      setUser(next);
      setLoading(false);
    });
    return () => unsub();
  }, [isBypass]);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    if (isBypass) return user ? 'dev-mock-id-token' : null;
    const current = getFirebaseAuth().currentUser;
    if (!current) return null;
    return current.getIdToken();
  }, [isBypass, user]);

  const signInWithGoogle = useCallback(async () => {
    setError(null);
    if (isBypass) {
      setUser(DEV_MOCK_USER);
      return;
    }
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      await signInWithPopup(getFirebaseAuth(), provider);
    } catch (err) {
      setError(mapAuthError(err));
      throw err;
    }
  }, [isBypass]);

  const signInWithGithub = useCallback(async () => {
    setError(null);
    if (isBypass) {
      setUser(DEV_MOCK_USER);
      return;
    }
    try {
      await signInWithPopup(getFirebaseAuth(), new GithubAuthProvider());
    } catch (err) {
      setError(mapAuthError(err));
      throw err;
    }
  }, [isBypass]);

  const sendMagicLink = useCallback(
    async (email: string) => {
      setError(null);
      const trimmed = email.trim();
      if (!trimmed) {
        setError('Enter your email address.');
        throw new Error('missing email');
      }
      if (isBypass) {
        setUser(DEV_MOCK_USER);
        return;
      }
      try {
        const actionCodeSettings = {
          url: `${window.location.origin}/`,
          handleCodeInApp: true,
        };
        await sendSignInLinkToEmail(getFirebaseAuth(), trimmed, actionCodeSettings);
        window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, trimmed);
      } catch (err) {
        setError(mapAuthError(err));
        throw err;
      }
    },
    [isBypass],
  );

  const completeMagicLinkIfPresent = useCallback(async (): Promise<boolean> => {
    if (isBypass) return false;
    const auth = getFirebaseAuth();
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return false;
    }
    setError(null);
    let email = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? '';
    if (!email) {
      email = window.prompt('Confirm your email for Zunelo sign-in') ?? '';
    }
    if (!email.trim()) {
      setError('Email confirmation required to finish sign-in.');
      return false;
    }
    try {
      await signInWithEmailLink(auth, email.trim(), window.location.href);
      window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    } catch (err) {
      setError(mapAuthError(err));
      return false;
    }
  }, [isBypass]);

  const signOut = useCallback(async () => {
    setError(null);
    if (isBypass) {
      setUser(null);
      return;
    }
    await firebaseSignOut(getFirebaseAuth());
  }, [isBypass]);

  const clearError = useCallback(() => setError(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      error,
      getIdToken,
      signInWithGoogle,
      signInWithGithub,
      sendMagicLink,
      completeMagicLinkIfPresent,
      signOut,
      clearError,
    }),
    [
      user,
      loading,
      error,
      getIdToken,
      signInWithGoogle,
      signInWithGithub,
      sendMagicLink,
      completeMagicLinkIfPresent,
      signOut,
      clearError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
